import { useState, useEffect } from "react";
import { Plus, X } from "lucide-react";
import Modal from "../ui/Modal";
import Input from "../ui/Input";
import Select from "../ui/Select";
import Button from "../ui/Button";
import OfferForm from "../offers/OfferForm";
import { toISO, today } from "../../lib/dateUtils";
import { COUNTRIES } from "../../lib/countries";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Format a dollar gap for inline validation messages. Strips trailing
// .00 on whole numbers so "Add $500 more" reads cleanly instead of "$500.00".
const formatGap = (n) => {
  const abs = Math.abs(Number(n) || 0);
  return abs % 1 === 0 ? `$${abs.toLocaleString()}` : `$${abs.toFixed(2)}`;
};

// New-client only: form returns this list to its parent so the parent can
// spawn scheduled payment records linked to the new client.
const blankSchedule = (startDate) => [{ date: startDate, amount: 0, note: "" }];

export default function AddClientForm({
  open, onClose, onSubmit, offers, settings, initial, onCreateOffer,
  onCreateScheduledPayments, existingPayments = [],
}) {
  const types = settings?.clientTypes?.length ? settings.clientTypes : ["Hotel", "Resort", "DMC", "Tour operator", "Other"];
  const sources = settings?.acquisitionSources?.length ? settings.acquisitionSources : ["Cold email", "Referral", "Inbound", "Other"];
  const funnels = settings?.funnels || [];
  const departments = settings?.departments?.length ? settings.departments : ["Cold Email"];
  const isEditing = !!initial;

  const blank = () => ({
    name: "",
    email: "",
    country: "",
    type: types[0] || "",
    primaryType: "recurring",
    primaryOfferId: offers.find((o) => o.type === "recurring")?.id || offers[0]?.id || "",
    startDate: toISO(today()),
    churnDate: "",
    status: "active",
    monthlyRate: 0,
    setupPrice: 0,
    acquisitionSource: sources[0] || "",
    funnel: "",
    department: "",
    notes: "",
  });
  const [form, setForm] = useState(() => initial || blank());
  const [errors, setErrors] = useState({});
  const [showNewOffer, setShowNewOffer] = useState(false);
  // Payment schedule is form-only state — only used on new client creation
  // to auto-spawn scheduled payments. Not persisted on the client itself.
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [schedule, setSchedule] = useState(() => blankSchedule(toISO(today())));

  useEffect(() => {
    if (open) {
      setForm(initial || blank());
      setErrors({});
      setScheduleEnabled(false);
      setSchedule(blankSchedule(initial?.startDate || toISO(today())));
    }
  }, [open, initial]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const recurringOffers = offers.filter((o) => o.type === "recurring");
  const oneOffOffers = offers.filter((o) => o.type === "oneoff");

  const handlePickOffer = (offerId) => {
    if (!offerId) {
      update("primaryOfferId", "");
      return;
    }
    const picked = offers.find((o) => o.id === offerId);
    if (picked) {
      setForm((f) => ({
        ...f,
        primaryOfferId: offerId,
        primaryType: picked.type,
      }));
    } else {
      update("primaryOfferId", offerId);
    }
  };

  const addScheduleRow = () => {
    setSchedule((s) => [...s, { date: form.startDate || toISO(today()), amount: 0, note: "" }]);
  };
  const updateScheduleRow = (i, patch) => {
    setSchedule((s) => s.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  };
  const removeScheduleRow = (i) => {
    setSchedule((s) => s.filter((_, idx) => idx !== i));
  };

  const scheduleTotal = schedule.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
  const setupPriceNum = Number(form.setupPrice) || 0;
  const price = Number(form.monthlyRate) || 0;
  // Sum of payments already on file for this client — paid AND pending/scheduled.
  // The schedule editor fills the gap between price and what's already accounted
  // for, so adding new scheduled payments doesn't double-bill.
  const alreadyPaid = existingPayments
    .filter((p) => p.status === "paid")
    .reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const alreadyPending = existingPayments
    .filter((p) => p.status !== "paid")
    .reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const alreadyBilled = alreadyPaid + alreadyPending;
  // What the new schedule rows must sum to. Clamped at 0 so over-billed
  // clients don't show a negative target.
  const priceTarget = Math.max(0, price - alreadyBilled);
  // When the schedule is enabled, its rows must add up exactly to the target.
  // Tolerance handles floating-point drift on cents.
  const scheduleMismatch = scheduleEnabled && price > 0
    ? Math.abs(scheduleTotal - priceTarget) > 0.01
    : false;
  const scheduleGap = priceTarget - scheduleTotal;

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.name.trim()) errs.name = "Name is required";
    if (!form.email.trim()) errs.email = "Email is required";
    else if (!EMAIL_RE.test(form.email.trim())) errs.email = "Not a valid email";
    if (!form.startDate) errs.startDate = "Start date is required";
    if (scheduleEnabled && price <= 0) errs.monthlyRate = "Set a price before splitting payments";
    if (scheduleEnabled && price > 0 && priceTarget <= 0) {
      errs.schedule = `This client is already billed for the full price ($${alreadyBilled.toLocaleString()}). Nothing left to schedule.`;
    } else if (scheduleMismatch) {
      errs.schedule = `Schedule rows must total ${formatGap(priceTarget)} to fill the remaining balance. You are ${scheduleGap > 0 ? `${formatGap(scheduleGap)} short` : `${formatGap(-scheduleGap)} over`}.`;
    }
    if (Object.keys(errs).length) { setErrors(errs); return; }

    const cleanedClient = {
      ...form,
      email: form.email.trim().toLowerCase(),
      churnDate: form.churnDate || null,
      monthlyRate: Number(form.monthlyRate) || 0,
      setupPrice: setupPriceNum,
    };
    const result = onSubmit(cleanedClient);
    const clientId = result?.id || initial?.id;

    if (onCreateScheduledPayments && clientId) {
      const rows = [];
      // Setup fee — only on NEW client creation (avoid spawning duplicates
      // on every edit if you change the setup price).
      if (!isEditing && setupPriceNum > 0) {
        rows.push({
          clientId,
          offerId: form.primaryOfferId || null,
          amount: setupPriceNum,
          bookedDate: form.startDate,
          dueDate: form.startDate,
          paidDate: null,
          status: "scheduled",
          note: "Setup fee",
        });
      }
      // Payment schedule — spawns from whatever rows you fill in, on both
      // new client AND edit. Form state resets each open so editing again
      // won't re-spawn anything you already saved.
      if (scheduleEnabled) {
        schedule.forEach((r) => {
          const amt = Number(r.amount) || 0;
          if (r.date && amt > 0) {
            rows.push({
              clientId,
              offerId: form.primaryOfferId || null,
              amount: amt,
              bookedDate: r.date,
              dueDate: r.date,
              paidDate: null,
              status: "scheduled",
              note: r.note || "",
            });
          }
        });
      }
      if (rows.length > 0) {
        onCreateScheduledPayments(rows);
      }
    }
    onClose();
  };

  const handleCreateOffer = (offerData) => {
    if (!onCreateOffer) return;
    const newOffer = onCreateOffer(offerData);
    if (newOffer && newOffer.type === form.primaryType) {
      update("primaryOfferId", newOffer.id);
    } else if (newOffer) {
      setForm((f) => ({ ...f, primaryType: newOffer.type, primaryOfferId: newOffer.id }));
    }
  };

  return (
    <>
      <Modal open={open} onClose={onClose} title={isEditing ? "Edit client" : "Add client"} size="lg">
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Input label="Name *" value={form.name} onChange={(e) => update("name", e.target.value)} error={errors.name} />
            <Input label="Email *" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} error={errors.email} placeholder="contact@business.com" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Select label="Country" value={form.country} onChange={(e) => update("country", e.target.value)} error={errors.country}>
              <option value="">Select country…</option>
              {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </Select>
            <Select label="Type" value={form.type} onChange={(e) => update("type", e.target.value)} options={types} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Select label="Acquisition source" value={form.acquisitionSource} onChange={(e) => update("acquisitionSource", e.target.value)} options={sources} />
            <Select label="Funnel" value={form.funnel} onChange={(e) => update("funnel", e.target.value)}>
              <option value="">— None —</option>
              {funnels.map((f) => <option key={f} value={f}>{f}</option>)}
            </Select>
          </div>
          {funnels.length === 0 && (
            <p className="text-xs text-brand-slate -mt-2">Add funnel options in Data → Taxonomies.</p>
          )}
          <Select
            label="Department"
            value={form.department || ""}
            onChange={(e) => update("department", e.target.value)}
          >
            <option value="">— None —</option>
            {departments.map((d) => <option key={d} value={d}>{d}</option>)}
          </Select>
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-brand-slate">Primary offer</span>
              {onCreateOffer && (
                <button
                  type="button"
                  onClick={() => setShowNewOffer(true)}
                  className="inline-flex items-center gap-1 text-xs text-brand-blue hover:text-brand-blue-dark font-medium"
                >
                  <Plus size={11} /> New offer
                </button>
              )}
            </div>
            <select
              value={form.primaryOfferId || ""}
              onChange={(e) => handlePickOffer(e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-blue/30 dark:bg-[var(--bg-soft)] dark:border-[var(--border-soft)] dark:text-[var(--text-primary)] ${errors.primaryOfferId ? "border-red-400" : "border-gray-200"}`}
            >
              <option value="">— Select an offer —</option>
              {recurringOffers.length > 0 && (
                <optgroup label="Recurring">
                  {recurringOffers.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </optgroup>
              )}
              {oneOffOffers.length > 0 && (
                <optgroup label="One-off">
                  {oneOffOffers.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </optgroup>
              )}
              {offers.length === 0 && <option value="" disabled>No offers yet — click "+ New offer"</option>}
            </select>
            <p className="text-xs text-brand-slate mt-1">Recurring vs one-off is set on the Offers tab.</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Input
              label={form.primaryType === "recurring" ? "Monthly rate (USD)" : "Deal value (USD)"}
              type="number"
              step="0.01"
              value={form.monthlyRate}
              onChange={(e) => update("monthlyRate", e.target.value)}
              placeholder={form.primaryType === "recurring" ? "What this client pays per month" : "Total deal value"}
            />
            <Input
              label="Setup fee (USD)"
              type="number"
              step="0.01"
              value={form.setupPrice}
              onChange={(e) => update("setupPrice", e.target.value)}
              placeholder="0"
            />
          </div>
          <p className="text-xs text-brand-slate -mt-2">
            {form.primaryType === "recurring"
              ? "Monthly rate is what this client pays per month (MRR). Setup fee is a one-time first-month charge — auto-scheduled on the start date if greater than 0."
              : "Deal value is the total for this one-off engagement. Setup fee, if any, is a separate one-time charge."}
          </p>

          <div className="grid grid-cols-2 gap-3">
            <Input label="Start date *" type="date" value={form.startDate} onChange={(e) => update("startDate", e.target.value)} error={errors.startDate} />
            <Input
              label="End date (optional)"
              type="date"
              value={form.churnDate || ""}
              onChange={(e) => update("churnDate", e.target.value)}
            />
          </div>
          <p className="text-xs text-brand-slate -mt-2">Leave end date empty for active clients. Setting it doesn't auto-cancel — change the Status separately.</p>

          <div className="border-t border-gray-100 dark:border-[var(--border-soft)] pt-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={scheduleEnabled}
                onChange={(e) => setScheduleEnabled(e.target.checked)}
                className="cursor-pointer"
              />
              <span className="text-xs font-medium text-brand-slate">
                {isEditing ? "Add scheduled payments to this client" : "Split into multiple scheduled payments"}
              </span>
            </label>
            <p className="text-xs text-brand-slate ml-6 mt-0.5">
              {isEditing
                ? "Each row becomes a new scheduled payment. The editor clears after save — won't duplicate on next edit."
                : "Add date + amount rows below. Each becomes a scheduled payment for forecasting and AR tracking."}
            </p>

              {scheduleEnabled && (
                <div className="mt-3 space-y-2 ml-6">
                  {schedule.map((row, i) => (
                    <div key={i} className="flex items-center gap-1.5">
                      <input
                        type="date"
                        value={row.date || ""}
                        onChange={(e) => updateScheduleRow(i, { date: e.target.value })}
                        className="px-2 py-1.5 border border-gray-200 rounded text-xs bg-white dark:bg-[var(--bg-soft)] dark:border-[var(--border-soft)] dark:text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-brand-blue/30"
                      />
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={row.amount ?? 0}
                        onChange={(e) => updateScheduleRow(i, { amount: e.target.value })}
                        placeholder="Amount"
                        className="w-28 px-2 py-1.5 text-right border border-gray-200 rounded text-xs bg-white dark:bg-[var(--bg-soft)] dark:border-[var(--border-soft)] dark:text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-brand-blue/30"
                      />
                      <input
                        type="text"
                        value={row.note || ""}
                        onChange={(e) => updateScheduleRow(i, { note: e.target.value })}
                        placeholder="Note (optional)"
                        className="flex-1 px-2 py-1.5 border border-gray-200 rounded text-xs bg-white dark:bg-[var(--bg-soft)] dark:border-[var(--border-soft)] dark:text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-brand-blue/30"
                      />
                      <button
                        type="button"
                        onClick={() => removeScheduleRow(i)}
                        className="p-1.5 rounded hover:bg-red-50 text-red-600"
                        title="Remove row"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                  <div className="flex items-center justify-between pt-1">
                    <button
                      type="button"
                      onClick={addScheduleRow}
                      className="inline-flex items-center gap-1 text-xs text-brand-blue hover:text-brand-blue-dark font-medium"
                    >
                      <Plus size={11} /> Add payment
                    </button>
                    <span className="text-[11px] text-brand-slate">
                      Total: <span className={`font-medium ${scheduleMismatch ? "text-red-600" : "text-emerald-600"}`}>${scheduleTotal.toLocaleString()}</span>
                      <span className="text-brand-slate"> / ${priceTarget.toLocaleString()} remaining</span>
                    </span>
                  </div>
                  {alreadyBilled > 0 && (
                    <div className="text-[11px] text-brand-slate pt-0.5">
                      Price: ${price.toLocaleString()} · Already on file: ${alreadyBilled.toLocaleString()}
                      {alreadyPaid > 0 && <span> ({alreadyPaid.toLocaleString()} paid{alreadyPending > 0 ? `, ${alreadyPending.toLocaleString()} pending` : ""})</span>}
                      {alreadyPaid === 0 && alreadyPending > 0 && <span> (all pending)</span>}
                    </div>
                  )}
                  {price > 0 && priceTarget > 0 && scheduleMismatch && (
                    <div className="text-[11px] text-red-600 font-medium pt-0.5">
                      {scheduleGap > 0
                        ? `Add ${formatGap(scheduleGap)} more to match the remaining balance.`
                        : `Reduce ${formatGap(-scheduleGap)} to match the remaining balance.`}
                    </div>
                  )}
                  {price > 0 && priceTarget > 0 && !scheduleMismatch && schedule.length > 1 && (
                    <div className="text-[11px] text-emerald-600 font-medium pt-0.5">
                      Schedule fills the remaining balance exactly.
                    </div>
                  )}
                  {price > 0 && priceTarget === 0 && (
                    <div className="text-[11px] text-brand-slate font-medium pt-0.5">
                      Nothing left to schedule — the full ${price.toLocaleString()} is already on file.
                    </div>
                  )}
                  {errors.schedule && (
                    <div className="text-[11px] text-red-600 pt-0.5">{errors.schedule}</div>
                  )}
                </div>
              )}
          </div>

          <label className="block">
            <span className="block text-xs font-medium text-brand-slate mb-1">Notes</span>
            <textarea
              rows={2}
              value={form.notes}
              onChange={(e) => update("notes", e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-blue/30 dark:bg-[var(--bg-soft)] dark:border-[var(--border-soft)] dark:text-[var(--text-primary)]"
            />
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" type="button" onClick={onClose}>Cancel</Button>
            <Button type="submit">{isEditing ? "Save" : "Add client"}</Button>
          </div>
        </form>
      </Modal>

      {onCreateOffer && (
        <OfferForm
          open={showNewOffer}
          onClose={() => setShowNewOffer(false)}
          onSubmit={handleCreateOffer}
          initial={{ name: "", type: form.primaryType, description: "" }}
        />
      )}
    </>
  );
}